import { FC, useCallback, useMemo } from 'react';

import { Document } from '@/shared/api/transcriptor/transcriptorApi';
import { transcriptorApi } from '@/shared/api/transcriptor/transcriptorApi';
import { useAppDispatch } from '@/shared/hooks';

import {
  TranscriptorConsultation,
  transcriptorModel,
} from '@/entities/transcriptor';

import { ConsultationNode, dentalConsultationMessages } from '../../config';
import { convertNodesToDocumentUpdate, parseSummaryData } from '../../lib';
import { ConsultationEmptyState } from '../ConsultationEmptyState/ConsultationEmptyState';
import { ConsultationWidget } from '../ConsultationWidget/ConsultationWidget';

type GenericDocumentWidgetProps = {
  consultation?: TranscriptorConsultation | null;
  document?: Document | null;
  onRegenerate?: () => void;
};

export const GenericDocumentWidget: FC<GenericDocumentWidgetProps> = ({
  consultation,
  document,
  onRegenerate,
}) => {
  const dispatch = useAppDispatch();

  const data = useMemo(
    () => (document ? parseSummaryData(document) : []),
    [document],
  );

  const handleSave = useCallback(
    async (editedData: ConsultationNode[]) => {
      if (!consultation?.id) {
        throw new Error('Consultation ID is missing');
      }
      if (!document?.id) {
        throw new Error('Document is not yet generated');
      }

      const updateRequest = convertNodesToDocumentUpdate(editedData);
      await transcriptorApi.updateConsultationDocument(
        consultation.id,
        document.id,
        updateRequest,
      );

      dispatch(
        transcriptorModel.thunks.fetchConsultationById({
          consultationId: consultation.id,
          patientId: consultation.patientId,
        }),
      );
    },
    [consultation?.id, consultation?.patientId, document?.id, dispatch],
  );

  if (!document || data.length === 0) {
    return (
      <ConsultationEmptyState
        status={consultation?.status}
        documentStatus={document?.status}
        documentError={document?.error}
        noDataMessage={dentalConsultationMessages.noSummaryAvailable}
        onRegenerate={onRegenerate}
      />
    );
  }

  return (
    <ConsultationWidget
      data={data}
      showActions
      widgetType="summary"
      transcriptChunks={consultation?.transcriptChunks}
      onSave={handleSave}
    />
  );
};
